<template>
  <v-container fluid>
    <v-container class="d-flex justify-space-between align-center" fluid>
      최종 갱신: {{ updatedAt.toLocaleString() }}
      <v-spacer></v-spacer>
      <v-btn icon @click="export2csv"><v-icon>mdi-download</v-icon></v-btn>
      <v-btn icon @click="fetchDays"><v-icon>mdi-refresh</v-icon></v-btn>
    </v-container>

    <v-row>
      <v-col cols="12" sm="4">
        <date-picker
          v-model="selectedDate"
          label="조회 날짜"
          hide-details
          outlined></date-picker>
      </v-col>
      <v-col cols="12" sm="4">
        <v-autocomplete
          v-model="customFilters.group"
          :items="groups"
          label="노선"
          hide-details
          clearable
          outlined></v-autocomplete>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="customFilters.plateNo"
          label="차량번호"
          hide-details
          clearable
          outlined></v-text-field>
      </v-col>
    </v-row>

    <v-data-table
      :items="items"
      :headers="headers"
      :search="search"
      :custom-filter="customFilter"
      :loading="loading"
      class="text-no-wrap"
    >
      <template v-slot:item.plateNo="{ item }">
        <a @click="showInfo(item)">{{ item.plateNo }}</a>
      </template>
      <template v-slot:item.fuelType="{ item }">
        <fueltype-chip :fueltype="item.fuelType"></fueltype-chip>
      </template>
    </v-data-table>

    <v-dialog v-model="show" max-width="500">
      <v-card v-if="show">
        <v-toolbar color="primary" dark>
          <v-toolbar-title>
            {{ selectedDevice.plateNo }}
          </v-toolbar-title>
        </v-toolbar>
        <v-container>
          <v-row no-gutters v-for="header in headers" :key="header.value">
            <v-col cols="6"><span class="subtitle-2">{{ header.text }}</span></v-col>
            <v-col cols="6">{{ get(selectedDevice, header.value) }}</v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { getDayList } from '@/api'
import { getDustReduced } from '@/js/utils'
import get from 'lodash/get'
import export2csv from '@/utils/export2csv'

export default {
  data () {
    return {
      days: [],
      updatedAt: new Date(),
      total: 0,
      loading: false,
      show: false,
      selectedDevice: null,
      selectedDate: this.$moment().format('YYYY-MM-DD'),
      customFilters: {
        group: '',
        plateNo: ''
      },
      headers: [
        {
          text: '노선',
          value: 'group.name'
        },
        {
          text: '차량번호',
          value: 'plateNo'
        },
        {
          text: '유종',
          value: 'fuelType'
        },
        {
          text: '운행거리 (Km)',
          value: 'trip'
        },
        {
          text: '운행시간',
          value: 'runTime'
        },
        {
          text: '연비 (Km)',
          value: 'fuelEfficiency',
          tooltip: '단위연료당'
        },
        {
          text: '공회전율 (%)',
          value: 'idleRate'
        },
        {
          text: '공회전 정지율 (%)',
          value: 'idleStopRate'
        },
        {
          text: '공회전 정지 횟수',
          value: 'idleStopCount'
        },
        {
          text: '절약 연료량',
          value: 'fuelReduced'
        },
        {
          text: '미세먼지 저감량 (g)',
          value: 'dustReduced'
        }
      ]
    }
  },
  computed: {
    search () {
      return JSON.stringify(this.customFilters)
    },
    groups () {
      return Object.entries(this.items.reduce((groups, row) => {
        if (!groups[row.group._id]) groups[row.group._id] = row.group.name

        return groups
      }, {})).map(([_id, name]) => {
        return {
          text: name,
          value: _id
        }
      })
    },
    filters () {
      const filters = {
        date: this.$moment(this.selectedDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      }

      return filters
    },
    query () {
      return {
        populate: '_dv',
        sort: '-ymd'
      }
    },
    items () {
      return this.days.map(day => {
        const group = {
          _id: get(day, '_dv.group._id', null),
          name: get(day, '_dv.group.name', '미지정')
        }
        const plateNo = get(day, '_dv.info.plateNo')
        const fuelType = ({
          0: '미지정',
          1: '경유',
          2: 'CNG'
        })[get(day, '_dv.fuelType', 0)]
        const date = this.$moment(get(day, 'ymd'), 'YYMMDD').format('YYYY. MM. DD.')
        const trip = get(day, 'dTrip', 0).toFixed(2)
        const fuel = get(day, 'dFuel', 0).toFixed(2)
        const fuelEfficiency = (trip / fuel || 0).toFixed(2)
        const idleTime = get(day, 'dIdleTime', 0)
        const runTime = get(day, 'dLap', 0)
        const idleRate = (idleTime / runTime * 100 || 0).toFixed(2)
        const idleStopTime = get(day, 'dIdleStopTime', 0)
        const idleGpsPtsTime = get(day, 'dIdleGpsPtsTime', 0)
        const idleStopRate = (idleStopTime / (idleStopTime + idleGpsPtsTime) * 100 || 0).toFixed(2)
        const fuelReduced = get(day, 'dIdleStopFuel', 0).toFixed(2)
        const dustReduced = getDustReduced(idleStopTime, fuelType).toFixed(2)
        const idleStopCount = get(day, 'dIdleStopCnt', 0)

        return {
          group,
          plateNo,
          fuelType,
          date,
          trip,
          fuelEfficiency,
          runTime: this.timeformat(runTime),
          idleRate,
          idleStopRate,
          fuelReduced,
          dustReduced,
          idleStopCount
        }
      })
    }
  },
  watch: {
    filters: {
      immediate: true,
      handler: 'fetchDays'
    }
  },
  methods: {
    get,
    showInfo (item) {
      this.selectedDevice = item
      this.$nextTick(() => {
        this.show = true
      })
    },
    export2csv () {
      const options = {
        filename: '운행이력',
        fields: [
          {
            label: '노선',
            value: 'group.name'
          },
          {
            label: '차량번호',
            value: 'plateNo'
          },
          {
            label: '유종',
            value: 'fuelType'
          },
          {
            label: '운행거리 (Km)',
            value: 'trip'
          },
          {
            label: '운행시간',
            value: 'runTime'
          },
          {
            label: '연비 (Km)',
            value: 'fuelEfficiency',
            tooltip: '단위연료당'
          },
          {
            label: '공회전율 (%)',
            value: 'idleRate'
          },
          {
            label: '공회전 정지율 (%)',
            value: 'idleStopRate'
          },
          {
            label: '공회전 정지 횟수',
            value: 'idleStopCount'
          },
          {
            label: '절약 연료량',
            value: 'fuelReduced'
          },
          {
            label: '미세먼지 저감량 (g)',
            value: 'dustReduced'
          }
        ]
      }

      const data = this.items

      export2csv(data, options)
    },
    async fetchDays () {
      if (this.loading) return
      this.loading = true

      try {
        const filters = this.filters
        const query = this.query

        const { days, total } = await getDayList(filters, query)
        this.days = days
        this.total = total
        this.updatedAt = new Date()
      } catch (err) {
        console.error(err)
      }

      this.loading = false
    },
    customFilter (value, search, item) {
      const { group = '', plateNo = '' } = JSON.parse(search)
      return value !== null &&
        typeof value === 'string' &&
        (!plateNo || item.plateNo.indexOf(plateNo) !== -1) &&
        (!group || item.group._id === group)
    },
    timeformat (seconds) {
      const hour = parseInt(seconds / 60 / 60, 10).toString().padStart(2, '0')
      const minute = parseInt((seconds / 60) % 60).toString().padStart(2, '0')
      const second = parseInt(seconds % 60).toString().padStart(2, '0')

      return `${hour}:${minute}:${second}`
    }
  }
}
</script>
